import {
  AI_VIEW_CONTEXT_SCHEMA_VERSION,
  AI_VIEW_MODE_READ,
  AI_VIEW_NAME_DASHBOARD,
  DashboardAIChart,
  DashboardAIContext,
  DashboardAIGroup,
  DashboardAIIndicator,
  DashboardAISection,
  DashboardAIViewContext,
  DashboardAISeries
} from "@/shared/models/AIViewContext";
import { CollectionFilter } from "@/modules/DashboardTemplate/models/CollectionFilter";
import { IndicatorOrTag } from "@/modules/DashboardTemplate/models/IndicatorOrTag";
import { SectionOfDashboard } from "@/modules/DashboardTemplate/models/SectionOfDashboard";

type SerializableValue = string | number;

interface DashboardContextInput {
  dashboardId: string;
  dashboardName: string;
  sections?: SectionOfDashboard[];
  capturedAt?: number;
}

const valueOf = (value: any): SerializableValue | undefined => {
  if (typeof value === "string" || typeof value === "number") return value;
  if (value && typeof value === "object") {
    const candidate = value.value ?? value.id ?? value.name;
    if (typeof candidate === "string" || typeof candidate === "number") {
      return candidate;
    }
  }
  return undefined;
};

const valuesOf = (values: any): SerializableValue[] => {
  if (!Array.isArray(values)) return [];

  const seriesWithData = values.find(
    item => item && typeof item === "object" && Array.isArray(item.data)
  );
  if (seriesWithData) return valuesOf(seriesWithData.data);

  return values
    .map(valueOf)
    .filter((value): value is SerializableValue => value !== undefined);
};

const seriesOf = (values: any): DashboardAISeries[] => {
  if (!Array.isArray(values)) return [];
  return values
    .filter(
      item => item && typeof item === "object" && Array.isArray(item.data)
    )
    .map(item => ({
      name: String(item.name || "Serie"),
      values: valuesOf(item.data)
    }));
};

const groupByOf = (groupBy: CollectionFilter["groupBy"]): string[] => {
  if (!Array.isArray(groupBy)) return [];
  return (groupBy as Array<string | { name?: string; id?: string }>)
    .map(item => (typeof item === "string" ? item : item.name || item.id))
    .filter((item): item is string => Boolean(item));
};

const normalizeGroups = (
  groups: any,
  labels: SerializableValue[],
  values: SerializableValue[]
): DashboardAIGroup[] => {
  const source = Array.isArray(groups)
    ? groups
    : groups && typeof groups === "object"
    ? Object.keys(groups).map(label => ({ label, value: groups[label] }))
    : [];

  const normalized: DashboardAIGroup[] = source.reduce<DashboardAIGroup[]>(
    (result, group) => {
      const label = valueOf(group?.label ?? group?.name ?? group?._key);
      const value = valueOf(group?.value ?? group?.count ?? group?.total);
      if (label === undefined || value === undefined) return result;
      const percentage = Number(group?.percentage);
      result.push({
        label: String(label),
        value,
        ...(Number.isFinite(percentage) ? { percentage } : {})
      });
      return result;
    },
    []
  );

  if (normalized.length > 0) return normalized;

  const numericValues = values.map(Number);
  const total = numericValues.every(Number.isFinite)
    ? numericValues.reduce((sum, value) => sum + value, 0)
    : 0;
  return labels.reduce<DashboardAIGroup[]>((result, label, index) => {
    const value = values[index];
    if (value === undefined) return result;
    const numericValue = Number(value);
    result.push({
      label: String(label),
      value,
      ...(total > 0 && Number.isFinite(numericValue)
        ? { percentage: Number(((numericValue / total) * 100).toFixed(2)) }
        : {})
    });
    return result;
  }, []);
};

const normalizeChart = (collection: CollectionFilter): DashboardAIChart => {
  const chartData = collection.chartData || {};
  const labels = valuesOf(chartData.xAxis ?? chartData.labels);
  const values = valuesOf(chartData.data ?? chartData.values);
  const series = seriesOf(chartData.data ?? chartData.values);
  const collectionInfo = collection.collection?.[0];
  const id = collection.id;

  return {
    ...(id ? { id } : {}),
    name: collection.chartName || collection.name || "",
    type: collection.chartType || "",
    ...(collectionInfo?.name || collectionInfo?.id
      ? { collection: collectionInfo.name || collectionInfo.id }
      : {}),
    ...(groupByOf(collection.groupBy).length > 0
      ? { groupBy: groupByOf(collection.groupBy) }
      : {}),
    labels: labels.map(String),
    values,
    ...(series.length > 0 ? { series } : {}),
    groups: normalizeGroups(chartData.groups, labels, values)
  };
};

const normalizeIndicator = (item: IndicatorOrTag): DashboardAIIndicator => ({
  name: item.indicatorOrTagName || "",
  value: valueOf(item.value) ?? ""
});

const normalizeFilter = (filter: any) => {
  const field = valueOf(
    Array.isArray(filter?.field) ? filter.field[0] : filter?.field
  );
  const operator = valueOf(
    Array.isArray(filter?.operator) ? filter.operator[0] : filter?.operator
  );
  if (field === undefined || operator === undefined) return undefined;
  return {
    field: String(field),
    operator: String(operator),
    value: valuesOf(filter.value)
  };
};

const normalizeSection = (
  section: SectionOfDashboard,
  index: number
): DashboardAISection => ({
  name: section.name || `Sección ${index + 1}`,
  charts: (section.collections || [])
    .filter(collection => Boolean(collection))
    .map(normalizeChart),
  indicators: [...(section.tags || []), ...(section.indicators || [])]
    .filter(item => item.show)
    .map(normalizeIndicator)
});

export function createDashboardAIContext({
  dashboardId,
  dashboardName,
  sections = [],
  capturedAt = Date.now()
}: DashboardContextInput): DashboardAIViewContext {
  const data: DashboardAIContext = {
    dashboardId,
    dashboardName,
    sections: sections.map(normalizeSection)
  };

  const filters: Record<string, unknown> = {};
  sections.forEach((section, index) => {
    const sectionFilters = (section.collections || [])
      .flatMap(collection => collection.filters || [])
      .map(normalizeFilter)
      .filter(Boolean);
    if (sectionFilters.length > 0) {
      filters[section.name || `section-${index + 1}`] = sectionFilters;
    }
  });

  return {
    schemaVersion: AI_VIEW_CONTEXT_SCHEMA_VERSION,
    view: {
      name: AI_VIEW_NAME_DASHBOARD,
      mode: AI_VIEW_MODE_READ,
      recordId: dashboardId || undefined,
      title: dashboardName || undefined
    },
    filters,
    data,
    capturedAt
  };
}
